import { createProvider } from '@nvision/services/data-provider';
import { infiniteQueryOptions } from '@tanstack/react-query';

import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';

import type { ContractsSearchParams } from '../../models';
import { CACHE_KEYS } from '../../types';
import { logger } from '../../utils/logger';
import * as api from './api';
import { normalizeContracts } from './normalize';

export const fetchContracts = createProvider(api.fetchContracts)
  .andThen(normalizeContracts)
  .andCatch((error) => {
    if (error instanceof Error && error.name === 'AbortError') {
      logger.warn('Fetch contracts is aborted');
    } else {
      showNotification({ message: 'Fetch contracts was failed', type: NOTIFICATION_TYPES.ERROR });
      logger.error(`Fetch contracts was failed: ${error}`);
    }
    throw error;
  });

export const CONTRACTS_PAGE_SIZE = 10;

export const contractsInfiniteQueryOptions = (filters: ContractsSearchParams, enabled = true) =>
  // biome-ignore assist/source/useSortedKeys: TODO: wait for tanstack query update
  infiniteQueryOptions({
    enabled,
    initialPageParam: 0,
    queryFn: ({ pageParam, signal }) =>
      fetchContracts({ ...filters, skip: pageParam, take: CONTRACTS_PAGE_SIZE }, { signal }),
    queryKey: CACHE_KEYS.contracts(filters),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((count, page) => count + page.items.length, 0);

      return loadedCount < lastPage.totalCount ? loadedCount : undefined;
    },
    select: (data) => ({
      items: data.pages.flatMap((page) => page.items),
      totalCount: data.pages[0]?.totalCount ?? 0,
    }),
    // biome-ignore lint/style/noMagicNumbers: 1 hour
    staleTime: 1000 * 60 * 60,
  });
